import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "You can start investing with $100 by opening a free brokerage account, transferring $100, and buying a fractional share of a low-cost ETF like VOO (S&P 500) or VTI (US total market). The key is to start the habit, not maximise the first dollar. With $100 you build the muscle of choosing, buying, and holding — which is worth more than the return on your first deposit.";

const FAQS = [
  { q: 'Is $100 enough to actually make money?', a: 'In the short term, no. A 10% return on $100 is $10. But the habit of investing — and the compounding that follows when you keep adding — is what matters. The first $100 is the gateway.' },
  { q: 'What\'s the best brokerage for $100?', a: 'In the US: Fidelity, Charles Schwab, and Robinhood all support fractional shares with no account minimums and no commissions. Outside the US: Wealthsimple (Canada), Trading 212 (UK), Stake (AUS).' },
  { q: 'Should I buy individual stocks or an ETF first?', a: 'An ETF is the safer first purchase because it gives you instant diversification. Individual stocks can come later, once you understand what you\'re buying. See our guide on index funds vs stocks.' },
  { q: 'What about meme stocks or crypto?', a: 'Treat them as entertainment, not investment, especially with your first $100. Building a habit on something that swings 20% a week makes it harder to stay consistent.' },
  { q: 'How much should I add per month after the first $100?', a: 'Whatever you can sustain. Even $25/month invested into the S&P 500 from age 25 to 65 grows to roughly $90,000 at historical returns. The amount matters less than the consistency.' },
];

export default function HowToStartInvestingWith100DollarsPage() {
  return (
    <BlogArticleLayout
      slug="how-to-start-investing-with-100-dollars"
      title="How to Start Investing with $100"
      description="You don't need thousands to start. Here's a realistic step-by-step plan for putting your first $100 into the market this week."
      keywords="how to start investing with 100 dollars, investing with little money, beginner investing, how to invest 100 dollars, fractional shares, first investment"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['index-funds-vs-stocks', 'paper-trading', 'investing-mistakes-beginners-make']}
    >
      <section>
        <h2>Why start with $100?</h2>
        <p>
          The hardest part of investing is starting, not stock-picking. $100 is enough to make the decision real without putting anything serious at risk.
          You learn to navigate a brokerage, see how a position behaves day to day, and most importantly form the habit. The compound effect of decades of consistent investing dwarfs whatever clever trade you make on day one.
        </p>
      </section>

      <section>
        <h2>What's the step-by-step plan for $100?</h2>
        <ul>
          <li><strong>1. Pick a brokerage.</strong> US: Fidelity, Schwab, Robinhood. UK: Trading 212, Hargreaves Lansdown. AUS: CommSec, Stake. Look for $0 commission and fractional shares.</li>
          <li><strong>2. Open the account.</strong> 10–15 minutes of forms and ID upload. You'll need basic identity documents.</li>
          <li><strong>3. Transfer $100.</strong> Bank transfer is free; debit card top-up usually is too.</li>
          <li><strong>4. Buy a low-cost ETF.</strong> VOO, VTI, or your local equivalent. Use the full $100 (fractional shares allow it).</li>
          <li><strong>5. Set a recurring deposit.</strong> Even $25/month. This is where the real returns come from.</li>
        </ul>
      </section>

      <section>
        <h2>What should you actually buy with $100?</h2>
        <p>
          The default beginner pick is a broad-market index ETF:
        </p>
        <ul>
          <li><strong>VOO</strong> (Vanguard S&P 500) — 500 of the largest US companies. Expense ratio 0.03%.</li>
          <li><strong>VTI</strong> (Vanguard Total Market) — basically the entire US stock market.</li>
          <li><strong>VT</strong> (Vanguard Total World) — global diversification in one ticker.</li>
        </ul>
        <p>
          Why an ETF over a single stock? Because if your first $100 goes into one company that drops 30%, you might never invest again. Diversification keeps the early experience boring enough that you stick with it.
          See our deeper guide on <Link to="/blog/index-funds-vs-stocks">index funds vs individual stocks</Link>.
        </p>
      </section>

      <section>
        <h2>What mistakes should you avoid?</h2>
        <p>
          The classic first-$100 mistakes:
        </p>
        <ul>
          <li><strong>Day trading the first deposit.</strong> Commissions are zero, but the temptation to trade in and out is real and almost always loses.</li>
          <li><strong>Chasing meme stocks.</strong> The volatility makes consistency nearly impossible.</li>
          <li><strong>Picking based on a tip from social media.</strong> If you can't explain why you bought it, sell it.</li>
          <li><strong>Selling at the first 5% dip.</strong> The market drops 10%+ once a year on average. Volatility is the price of admission.</li>
        </ul>
        <p>
          More on this in our list of <Link to="/blog/investing-mistakes-beginners-make">10 investing mistakes beginners make</Link>.
        </p>
      </section>

      <section>
        <h2>Want to practise before risking real money?</h2>
        <p>
          Two free, low-stakes ways to build skill:
        </p>
        <ul>
          <li><strong><Link to="/blog/paper-trading">Paper trading</Link></strong> — simulate a real portfolio using virtual cash and live prices. Investopedia and MarketWatch both run free simulators.</li>
          <li><strong><Link to="/">Wallstreetle</Link></strong> — daily stock puzzles that teach you to read charts and metrics in 5 minutes a day.</li>
        </ul>
      </section>
    </BlogArticleLayout>
  );
}
